import React, { useEffect } from 'react';
import styles from './styles.module.scss';
import { useStore } from '../../../../../../stores/root';

// assets
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../../services/general/error';

// props

// components
import { CommandBar, ContextualMenu, ContextualMenuItemType, DirectionalHint, IColumn, ICommandBarItemProps, Icon, IContextualMenuItem, IContextualMenuProps, Link, PrimaryButton, SearchBox, SelectionMode, ShimmeredDetailsList, Stack } from '@fluentui/react';
import Text from './../../../../../typography/text';
import { IPaginationResourceShort } from '../../../../../../props/general';
import Pagination from '../../../../../uiframeworks/pagination';
import GlobalConfig from '../../../../../../config';
import { IIncomeInvoiceResourceShortProps } from '../../../../../../props/finance/incomes/invoices/invoice';
import IncomeInvoiceService from '../../../../../../services/finance/incomes/invoices/invoice';
import OrderService from '../../../../../../services/orders/order';
import Tag, { TTagVariant } from '../../../../../uiframeworks/tag';
import GeneralService from '../../../../../../services/general';
import IncomeInvoiceForm from '../form';
import PermissionsService from '../../../../../../services/permissions';
import IncomeInvoiceDetails from '../details';
import SelectOrder from '../../../../../uiframeworks/forms/selectOrder';
import moment from 'moment';
import Label from '../../../../../typography/label';
import ListItems from '../../../../../uiframeworks/listitems';
import { ListItemsColumnProps, ListItemsFilterByProps, ListItemsOrderByProps, ListItemsQuickFilterProps } from '../../../../../uiframeworks/listitems/props';
import SubsidiariesService from '../../../../../../services/data/subsidiaries';
import CompanyService from '../../../../../../services/users/company';

interface IncomeInvoicesListProps {
    qs?: string[];
    orderId?: string;
    invoices?: IIncomeInvoiceResourceShortProps[];
    columns?: TIncomeInvoiceListCoumn[];
    quickFilters?: ListItemsQuickFilterProps[];
    hideSearch?: boolean;
    hideCommandBar?: boolean;
    hideCreateButton?: boolean;
    variant?: 'card' | 'plain';
}

export type TIncomeInvoiceListCoumn = 'invoiceNumber' | 'orderNumber' | 'subsidiary' | 'customer' | 'invoiceDate' | 'relatedItems' | 'orderDetails' | 'status' | 'paid' | 'unpaid' | 'amount' | 'dueDate' | 'notes';

const IncomeInvoicesList: React.FC<IncomeInvoicesListProps> = (props: IncomeInvoicesListProps) => {
    const shownColumns: TIncomeInvoiceListCoumn[] = props.columns || [
        'invoiceNumber',
        'orderDetails',
        'relatedItems',
        'amount',
        'paid',
        'unpaid',
        'status',
        'notes',
        'dueDate'
    ];

    const { banner } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [invoices, setInvoices] = React.useState<IIncomeInvoiceResourceShortProps[]>(props.invoices || []);
    const [activeInvoice, setActiveInvoice] = React.useState<IIncomeInvoiceResourceShortProps | undefined>();
    const [pagination, setPagination] = React.useState<IPaginationResourceShort | undefined>();
    const [keyword, setKeyword] = React.useState<string>("");
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();
    const [filterBy, setFilterBy] = React.useState<ListItemsFilterByProps[] | undefined>();

    const [orderBy, setOrderBy] = React.useState<ListItemsOrderByProps | undefined>(undefined);

    const columns: ListItemsColumnProps[] = [
        {
            key: "invoiceNumber",
            name: "Invoice Number",
            minWidth: 125,
            maxWidth: 125,
            fieldName: 'id',
            enableOrderBy: true,
            onRender: (item: IIncomeInvoiceResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Link onClick={() => {
                        setActiveInvoice(item);
                        setActiveSurface('details');
                    }}><Text size={'small'}>#{IncomeInvoiceService.getInvoiceNumber(item.id || "")}</Text></Link>
                </Stack>
            }
        },
        {
            key: "orderNumber",
            name: "Order Number",
            minWidth: 135,
            maxWidth: 135,
            onRender: (item: IIncomeInvoiceResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    {item.order ? <Link href={`/orders/${item.order.id}`} target={'_blank'}>
                        <Text size={'small'}>#{OrderService.getOrderNumber(item.order.orderNumber)}</Text>
                    </Link> : null}
                    {!item.order ? <Tag size={'small'} variant={'error'} text={'!!! Not related to order !!!'} /> : null}
                </Stack>
            }
        },
        {
            key: "customer",
            name: "Customer",
            minWidth: 150,
            maxWidth: 150,
            enableFilter: true,
            onRetrieveFilterOptions: async () => {
                const companies = (await CompanyService.retrieve("top=all")).data;
                return companies.map((com) => {
                    return {
                        key: com.id,
                        title: com.name
                    }
                });
            },
            onRender: (item: IIncomeInvoiceResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    {item.company ? <Text size={'small'}>{item.company.name}</Text> : null}
                    {item.order ? <>
                        {item.order.company ? <Link href={`/users/companies/${item.order.company.id}`} target={'_blank'}>
                            <Text size={'small'}>{item.order.company.name}</Text>
                        </Link> : null}
                    </> : null}
                </Stack>
            }
        },
        {
            key: "subsidiary",
            name: "Subsidiary",
            fieldName: 'subsidiary_id',
            minWidth: 150,
            maxWidth: 150,
            enableFilter: true,
            onRetrieveFilterOptions: async () => {
                const subsidiaries = await SubsidiariesService.retrieve();
                return subsidiaries.map((sub) => {
                    return {
                        key: sub.id,
                        title: sub.name,
                        secondaryText: sub.npwp
                    }
                });
            },
            onRender: (item: IIncomeInvoiceResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    {item.subsidiary ? <Link href={`/settings/subsidiaries/${item.subsidiary.id}`} target={'_blank'}>
                        <Text size={'small'}>{item.subsidiary.name}</Text>
                    </Link> : null}
                    {item.order ? <Link href={`/settings/subsidiaries/${item.order.subsidiary.id}`} target={'_blank'}>
                        <Text size={'small'}>{item.order.subsidiary.name}</Text>
                    </Link> : null}
                </Stack>
            }
        },
        {
            key: "orderDetails",
            name: "Order Details",
            minWidth: 300,
            maxWidth: 300,
            onRender: (item: IIncomeInvoiceResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }} tokens={{ childrenGap: 8 }}>
                    {item.order ? <>
                        <SelectOrder selected={item.order}
                            disabled={true}
                            hideAddress
                            hideLabel
                            onChange={() => { }} />
                    </> : null}
                </Stack>
            }
        },
        {
            key: "relatedItems",
            name: "Related Items",
            minWidth: 350,
            onRender: (item: IIncomeInvoiceResourceShortProps) => {
                return <Stack horizontal tokens={{ childrenGap: 8 }} styles={{ root: { padding: '2px 0px' } }}>
                    {(item.items || []).map((invoiceItem) => {
                        return <Tag size={'small'} text={`${invoiceItem.name} (Rp. ${GeneralService.getNumberWithSeparator(invoiceItem.invoiceAmount)})`} />
                    })}
                    {(item.orderItems || []).map((orderItem) => {
                        const details = orderItem.addon || orderItem.container || orderItem.goods || orderItem.truck;

                        let text = "";
                        if (orderItem.addon) { text += orderItem.addon.name }
                        else if (orderItem.container) { text += `Container ${orderItem.container.containerNumber}`; }
                        else if (orderItem.truck) { text += `Truck ${orderItem.truck.vehicleRegistrationNumber || orderItem.truck.truck?.registrationNumber || ""}` }
                        else if (orderItem.goods) { text += orderItem.goods.goods.name }

                        text += ` (Rp. ${GeneralService.getNumberWithSeparator(details?.invoicePrice || "0")})`

                        return <Tag size={'small'} text={text} />
                    })}
                </Stack>
            }
        },
        {
            key: "notes",
            name: "Notes",
            minWidth: 150,
            onRender: (item: IIncomeInvoiceResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Text size={'small'}>{item.notes || "-"}</Text>
                </Stack>
            }
        },
        {
            key: "invoiceDate",
            name: "Created Date",
            minWidth: 100,
            maxWidth: 100,
            enableOrderBy: true,
            fieldName: 'created_at',
            onRender: (item: IIncomeInvoiceResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Text size={'small'}>{moment(item.createdAt).format("DD/MM/YYYY")}</Text>
                </Stack>
            }
        },
        {
            key: "dueDate",
            name: "Due Date",
            minWidth: 100,
            maxWidth: 100,
            enableOrderBy: true,
            fieldName: 'due_date',
            onRender: (item: IIncomeInvoiceResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Text size={'small'} className={Number(item.unpaid) > 0 ? 'color-red' : undefined}>{GeneralService.getFriendlyDateFormat(item.dueDate)}</Text>
                </Stack>
            }
        },
        {
            key: "amount",
            name: "Amount",
            minWidth: 200,
            maxWidth: 200,
            onRender: (item: IIncomeInvoiceResourceShortProps) => {
                let invoiceAmount = item.invoiceAmount;

                return <Stack styles={{ root: { padding: '4px 0px' } }} tokens={{ childrenGap: 5 }}>
                    <Text size={'small'}>Rp. {GeneralService.getNumberWithSeparator(Number(invoiceAmount))}</Text>
                    <Stack className={'divider'}></Stack>
                    <Stack>
                        <Stack horizontal tokens={{ childrenGap: 3 }}>
                            <Stack styles={{ root: { width: 50 } }}>
                                <Text size={'small'} className={'color-red'}>Unpaid</Text>
                            </Stack>
                            <Text size={'small'} className={'color-red'}>: Rp. {GeneralService.getNumberWithSeparator(item.unpaid)}</Text>
                        </Stack>
                        <Stack horizontal tokens={{ childrenGap: 3 }}>
                            <Stack styles={{ root: { width: 50 } }}>
                                <Text size={'small'} className={'color-green'}>Paid</Text>
                            </Stack>
                            <Text size={'small'} className={'color-green'}>: Rp. {GeneralService.getNumberWithSeparator(item.paid)}</Text>
                        </Stack>
                    </Stack>
                </Stack>
            }
        },
        {
            key: "status",
            name: "Status",
            minWidth: 100,
            maxWidth: 100,
            onRender: (item: IIncomeInvoiceResourceShortProps) => {
                let statusVariant: TTagVariant = 'success';
                if (item.status === 'Cancelled') {
                    statusVariant = 'error';
                } else if (item.status === 'Unpaid') {
                    statusVariant = 'warning';
                }

                return <Stack horizontal>
                    <Tag size={'small'} variant={statusVariant} text={item.status} />
                </Stack>
            }
        }
    ];

    useEffect(() => {
        if (!props.invoices) {
            _onRetrieveInvoices();
        } else {
            setLoaded(true);
        }
    }, [keyword, orderBy, filterBy]);

    useEffect(() => {
        const orderByQs = (props.qs || []).find((qs) => qs.toLowerCase().startsWith('order_by'));
        const orderByDirectionQs = (props.qs || []).find((qs) => qs.toLowerCase().startsWith('order_by_direction'));

        if (orderByQs) {
            setOrderBy({
                fieldName: orderByQs.split("=")[1],
                direction: orderByDirectionQs ? orderByDirectionQs.split("=")[1] as any : "asc"
            });
        }
    }, [props.qs]);

    const _onRetrieveInvoices = async (pageNumber?: number) => {
        try {
            setLoaded(false);

            const qs: string[] = props.qs ? JSON.parse(JSON.stringify(props.qs)) : [];
            qs.push(`top=${GlobalConfig.defaultTop}`);
            if (pageNumber) { qs.push(`page=${pageNumber}`); }
            if (keyword && keyword.trim() !== "") { qs.push(`search=${keyword}`) }
            if (orderBy) {
                const orderByIndex = qs.findIndex((qs) => qs.toLowerCase().startsWith('order_by='));
                const orderByDirectionIndex = qs.findIndex((qs) => qs.toLowerCase().startsWith('order_by_direction='));

                if (orderByIndex > -1) {
                    qs.splice(orderByIndex, 1, 'order_by=' + orderBy.fieldName);
                } else {
                    qs.push("order_by=" + orderBy.fieldName);
                }

                if (orderByDirectionIndex > -1) {
                    qs.splice(orderByDirectionIndex, 1, 'order_by_direction=' + orderBy.direction);
                } else {
                    qs.push("order_by_direction=" + orderBy.direction);
                }
            }
            (filterBy || []).forEach((f) => {
                qs.push(`${f.key}=${f.selected.map((sel) => sel.key).join(";")}`);
            });

            const results = await IncomeInvoiceService.retrieve(qs.join("&"));
            setInvoices(results.data);
            setPagination(results.pagination);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'retrieve_outcomes_list_error',
                text: 'Failed to retrieve outcomes list. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    const _onKeywordChanged = (value?: string) => {
        setLoaded(false);
        setKeyword(value || "");
    }

    const _onSurfaceDismissed = (refresh?: boolean) => {
        setActiveSurface(undefined);
        setActiveInvoice(undefined);

        if (refresh) { _onRetrieveInvoices() }
    }

    return <Stack className={styles.container} tokens={{ childrenGap: 20 }}>
        <ListItems key={"__sys_listitems_incomeInvoices"}
            pagination={pagination}
            shownColumns={props.columns || shownColumns}
            loaded={loaded}
            items={invoices}
            columns={columns}
            orderBy={orderBy}
            itemText={'invoices'}
            quickFilters={props.quickFilters}
            filterBy={filterBy}
            onOrderByChange={setOrderBy}
            onPageChange={_onRetrieveInvoices}
            onSearch={_onKeywordChanged}
            onFilterByChange={(filterBy) => setFilterBy([...filterBy])} />
        {activeSurface === 'details' && activeInvoice ? <IncomeInvoiceDetails invoiceId={activeInvoice.id} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'create' ? <IncomeInvoiceForm orderId={props.orderId} onDismissed={_onSurfaceDismissed} /> : null}
    </Stack >;
};

export default IncomeInvoicesList;
